import { Button, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { Dismiss16Regular } from '@fluentui/react-icons';
import type { Device, DeviceStatus } from '../../api/types';
import { PageAside } from '../../components/PageAside';
import { StatePill } from '../../components/StatePill';
import { formatBytes, formatDateTime, formatRelative } from '../../lib/format';
import { vt } from '../../theme';
import type { Lane, LiveSession, MapState } from './model';

const useStyles = makeStyles({
  head: { display: 'flex', alignItems: 'flex-start', padding: '22px 16px 14px 24px', borderBottom: `1px solid ${vt.surface2}` },
  titles: { flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', rowGap: '4px' },
  title: { margin: 0, fontSize: '16px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1, overflowWrap: 'anywhere' },
  sub: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11px', color: vt.fg3, overflowWrap: 'anywhere' },
  pills: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' },
  section: { padding: '16px 24px', display: 'flex', flexDirection: 'column', rowGap: '10px', borderBottom: `1px solid ${vt.surface2}` },
  label: { fontSize: '11.5px', fontWeight: tokens.fontWeightSemibold, color: vt.fg2 },
  kv: { display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr)', rowGap: '9px', columnGap: '12px', margin: 0 },
  k: { fontSize: '11.5px', color: vt.fg3 },
  v: { margin: 0, fontSize: '11.5px', color: vt.fg1, textAlign: 'right', overflowWrap: 'anywhere' },
  tags: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  tag: {
    height: '22px',
    lineHeight: '22px',
    paddingInline: '10px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: vt.surface3,
    border: `1px solid ${vt.surface2}`,
    fontSize: '11px',
    color: vt.fg2,
  },
  none: { fontSize: '11.5px', color: vt.fg4 },
  session: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11px', color: vt.fg2 },
  sessionPath: { color: vt.cyan },
});

const STATE_PILL: Record<Exclude<MapState, 'live'>, { state: 'running' | 'stopped'; label: string }> = {
  online: { state: 'running', label: 'Online' },
  idle: { state: 'stopped', label: 'Idle' },
  offline: { state: 'stopped', label: 'Offline' },
};

export interface InspectorProps {
  device: Device;
  lane: Lane | undefined;
  state: MapState;
  status: DeviceStatus | undefined;
  /** Live sessions with this device at either end. */
  sessions: LiveSession[];
  now: number;
  onClose: () => void;
}

/** The device the map has selected: the facts the API holds about it (mockup 02's right pane). */
export function Inspector({ device: d, lane, state, status, sessions, now, onClose }: InspectorProps) {
  const s = useStyles();
  // Live is a kind of online: the presence pill says online, the live pill says what is moving.
  const pill = STATE_PILL[state === 'live' ? 'online' : state];
  const rows: [string, string][] = [
    ['Network', lane ? `${lane.name} · ${lane.cidr}` : (d.network ?? 'legacy')],
    ['Address', d.overlayIp],
    ['Assignment', d.addressMode === 'reserved' ? 'reserved' : 'automatic'],
    ['Last handshake', formatRelative(status?.lastHandshakeAt ?? null, now)],
    ['Received', status ? formatBytes(status.rx) : '–'],
    ['Sent', status ? formatBytes(status.tx) : '–'],
    ['Enrolled', [formatDateTime(d.createdAt), d.ownerName ?? d.ownerSub].join(' · ')],
  ];

  return (
    <PageAside label="Device">
      <div className={s.head}>
        <div className={s.titles}>
          <h2 className={s.title}>{d.name}</h2>
          <span className={s.sub}>
            {d.overlayIp} · {d.id}
          </span>
          <div className={s.pills}>
            <StatePill state={pill.state} label={pill.label} />
            {sessions.length > 0 && (
              <StatePill state="live" label={sessions.length === 1 ? `Live · ${sessions[0]!.service}` : `${sessions.length} live sessions`} />
            )}
          </div>
        </div>
        <Tooltip content="Close" relationship="label">
          <Button appearance="subtle" shape="circular" size="small" icon={<Dismiss16Regular />} onClick={onClose} />
        </Tooltip>
      </div>

      <div className={s.section}>
        <dl className={s.kv}>
          {rows.map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt className={s.k}>{k}</dt>
              <dd className={s.v} data-kv={k}>
                {v}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <div className={s.section}>
        <span className={s.label}>Tags</span>
        {d.tags.length ? (
          <div className={s.tags}>
            {d.tags.map((t) => (
              <span key={t} className={s.tag}>
                tag:{t}
              </span>
            ))}
          </div>
        ) : (
          <span className={s.none}>No tags</span>
        )}
      </div>

      {sessions.length > 0 && (
        <div className={s.section}>
          <span className={s.label}>Live sessions</span>
          {sessions.map((x) => (
            <span key={x.id} className={s.session}>
              <span className={s.sessionPath}>{x.path}</span> · {x.meta}
            </span>
          ))}
        </div>
      )}
    </PageAside>
  );
}
